import { createContext, useCallback, useContext, useRef, useState } from 'react'

const ToastContext = createContext(null)

let idCounter = 0

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const timers = useRef(new Map())

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)))
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
      timers.current.delete(id)
    }, 300)
  }, [])

  const showToast = useCallback(
    (message, variant = 'default') => {
      const id = ++idCounter
      const duration = variant === 'notification' ? 3000 : 3500
      setToasts((prev) => [...prev, { id, message, variant, leaving: false }])
      const timer = window.setTimeout(() => dismiss(id), duration)
      timers.current.set(id, timer)
    },
    [dismiss],
  )

  const showNotification = useCallback((message) => showToast(message, 'notification'), [showToast])

  return (
    <ToastContext.Provider value={{ showToast, showNotification }}>
      {children}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2" id="toast-container">
        {toasts.map((t) =>
          t.variant === 'notification' ? (
            <div
              key={t.id}
              className={`bg-astroDeep border border-goldPrimary/30 text-goldPrimary px-4 py-3 rounded-xl shadow-2xl text-xs tracking-wider uppercase font-semibold transform transition-all duration-300 ${
                t.leaving ? 'translate-y-4 opacity-0' : 'translate-y-0 opacity-100'
              }`}
            >
              {t.message}
            </div>
          ) : (
            <div
              key={t.id}
              className={`bg-astroDeep/90 backdrop-blur border border-white/10 text-white px-5 py-3 rounded-xl shadow-2xl text-xs tracking-wider flex items-center gap-2 transform transition-all duration-300 ${
                t.leaving ? 'translate-y-2 opacity-0' : 'translate-y-0 opacity-100'
              }`}
            >
              <span>✨</span>
              <span>{t.message}</span>
            </div>
          ),
        )}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within a ToastProvider')
  return ctx
}
